// Menü an einem Knopf (z. B. „…“, „Anrufen ▾“). Pfeiltasten, Enter, Escape.
import { useEffect, useRef, useState, type ReactNode } from "react";
import { Ausklapp } from "./Ausklapp";
import { Icon, type IconName } from "./Icon";

export interface MenuePunkt {
  label: string;
  icon?: IconName;
  onClick?: () => void;
  gefahr?: boolean;
  deaktiviert?: boolean;
  hinweis?: string;
  /** Überschrift statt Eintrag */
  ueberschrift?: boolean;
  /** Trennlinie vor diesem Eintrag */
  trenner?: boolean;
  rechts?: ReactNode;
}

interface Props {
  punkte: MenuePunkt[];
  /** Inhalt des Knopfs */
  children: ReactNode;
  knopfKlasse?: string;
  titel?: string;
  ausrichtung?: "start" | "end";
  breite?: number;
  deaktiviert?: boolean;
}

export function MenueKnopf({ punkte, children, knopfKlasse = "knopf", titel, ausrichtung = "start", breite, deaktiviert }: Props) {
  const [offen, setOffen] = useState(false);
  const [knopf, setKnopf] = useState<HTMLButtonElement | null>(null);
  return (
    <>
      <button
        ref={setKnopf}
        type="button"
        className={knopfKlasse}
        title={titel}
        aria-label={titel}
        aria-haspopup="menu"
        aria-expanded={offen}
        disabled={deaktiviert}
        onClick={(e) => {
          e.stopPropagation();
          setOffen((o) => !o);
        }}
      >
        {children}
      </button>
      <Ausklapp anker={knopf} offen={offen} onSchliessen={() => setOffen(false)} ausrichtung={ausrichtung} breite={breite} rolle="menu">
        <MenueListe punkte={punkte} schliessen={() => setOffen(false)} />
      </Ausklapp>
    </>
  );
}

export function MenueListe({ punkte, schliessen }: { punkte: MenuePunkt[]; schliessen: () => void }) {
  const waehlbar = punkte.map((p, i) => (!p.ueberschrift && !p.deaktiviert && p.onClick ? i : -1)).filter((i) => i >= 0);
  const [aktiv, setAktiv] = useState(-1);
  const liste = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const el = liste.current;
    el?.focus({ preventScroll: true });
    // Absicherung: steht die Fläche beim Einhängen noch nicht im Dokument, im nächsten Takt erneut
    const t = window.setTimeout(() => {
      if (el && el.isConnected && !el.contains(document.activeElement)) el.focus({ preventScroll: true });
    }, 0);
    return () => window.clearTimeout(t);
  }, []);
  const ausfuehren = (p: MenuePunkt) => {
    schliessen();
    p.onClick?.();
  };
  return (
    <div
      ref={liste}
      className="menue"
      tabIndex={-1}
      onKeyDown={(e) => {
        if (e.key === "ArrowDown" || e.key === "ArrowUp") {
          e.preventDefault();
          const pos = waehlbar.indexOf(aktiv);
          const neu = e.key === "ArrowDown" ? waehlbar[(pos + 1) % waehlbar.length] : waehlbar[(pos - 1 + waehlbar.length) % waehlbar.length];
          setAktiv(neu ?? -1);
        } else if (e.key === "Enter" && aktiv >= 0) {
          e.preventDefault();
          ausfuehren(punkte[aktiv]);
        }
      }}
    >
      {punkte.map((p, i) =>
        p.ueberschrift ? (
          <div key={i} className={`menue__ueberschrift${p.trenner ? " menue__trenner" : ""}`}>
            {p.label}
          </div>
        ) : (
          <button
            key={i}
            type="button"
            role="menuitem"
            className={`menue__punkt${p.gefahr ? " menue__punkt--gefahr" : ""}${p.trenner ? " menue__trenner" : ""}${aktiv === i ? " menue__punkt--aktiv" : ""}`}
            disabled={p.deaktiviert || !p.onClick}
            title={p.hinweis}
            onMouseEnter={() => setAktiv(i)}
            onClick={() => ausfuehren(p)}
          >
            {p.icon ? <Icon name={p.icon} groesse={16} /> : null}
            <span className="menue__text">{p.label}</span>
            {p.rechts ? <span className="menue__rechts">{p.rechts}</span> : null}
          </button>
        ),
      )}
    </div>
  );
}

/** Kontextmenü (Rechtsklick) an der Mausposition. `ort` = null: geschlossen. */
export function KontextMenue({ ort, punkte, onSchliessen, breite = 220 }: { ort: { x: number; y: number } | null; punkte: MenuePunkt[]; onSchliessen: () => void; breite?: number }) {
  const [anker, setAnker] = useState<HTMLSpanElement | null>(null);
  return (
    <>
      {ort ? <span ref={setAnker} className="kontext-anker" style={{ left: ort.x, top: ort.y }} aria-hidden="true" /> : null}
      <Ausklapp anker={ort ? anker : null} offen={!!ort && !!anker} onSchliessen={onSchliessen} breite={breite} rolle="menu">
        <MenueListe punkte={punkte} schliessen={onSchliessen} />
      </Ausklapp>
    </>
  );
}
